import {PhoneHQSwitch} from "ch3-ui-lib";
import {AudioLines} from "lucide-react";
import {type ReactNode} from "react";
import AudioPlayer from "../../components/AudioPlayer/AudioPlayer";

export const lucide = '!p-0 shrink-0 !text-current';

/** One block of the drawer: 15px title, muted description, content. */
export const QueueSection = ({title, description, children}: { title: string; description?: ReactNode; children: ReactNode }) => (
    <section aria-label={title} className="flex flex-col gap-2.5 py-1">
        <div>
            <h3 className="m-0 mb-0.5 text-[15px] font-semibold leading-5 text-ink">{title}</h3>
            {description && <p className="m-0 text-footnote leading-[18px] text-ink-muted">{description}</p>}
        </div>
        {children}
    </section>
);

/** White card with a 1px line border and 12px radius (DS list card). */
export const QueueCard = ({children, className = ''}: { children: ReactNode; className?: string }) => (
    <div className={`overflow-hidden rounded-xl border border-solid border-line bg-surface ${className}`}>{children}</div>
);

/** Title, description and a switch on the right (Phone tree / Greeting message rows). */
export const ToggleRow = ({title, description, checked, onChange, trailing}: {
    title: string;
    description?: string;
    checked: boolean;
    onChange: () => void;
    trailing?: ReactNode;
}) => (
    <div className="flex min-h-12 items-center gap-3 py-2 pl-4 pr-3.5">
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="text-callout text-ink">{title}</span>
            {description && <span className="text-footnote text-ink-muted">{description}</span>}
        </span>
        {trailing}
        <Toggle label={title} checked={checked} onChange={onChange}/>
    </div>
);

/** DS switch: 44×26 track, brand when on, 22px white knob. */
export const Toggle = ({label, checked, onChange}: { label: string; checked: boolean; onChange: () => void }) => (
    <PhoneHQSwitch aria-label={label} checked={checked} onCheckedChange={onChange}/>
);

/** Recorded/uploaded message: header with the message name, then the player. */
export const AudioCard = ({title, src}: { title: string; src: string }) => (
    <QueueCard>
        <div className="flex items-center gap-2 border-0 border-b border-solid border-line px-3 py-2.5 text-callout text-ink">
            <AudioLines size={16} aria-hidden="true" className={`${lucide} text-ink-muted`}/>
            {title}
        </div>
        <div className="px-3 py-2.5">
            <AudioPlayer src={src}/>
        </div>
    </QueueCard>
);

/** Small secondary button used for "Change" / "Set up" next to a message. */
export const SmallButton = ({children, onClick, ...rest}: { children: ReactNode; onClick: () => void } & React.ButtonHTMLAttributes<HTMLButtonElement>) => (
    <button
        type="button"
        onClick={onClick}
        {...rest}
        className="box-border inline-flex h-8 flex-none cursor-pointer items-center rounded-chip border border-solid border-fill-subtle bg-surface px-3 text-callout font-semibold text-ink hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus"
    >
        {children}
    </button>
);
